import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Reorder, useDragControls } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { Storefront, QrCode, Receipt, Ticket, ArrowSquareOut, DownloadSimple, DotsSixVertical } from "@phosphor-icons/react";

const money = (c) => `$${(c / 100).toFixed(2)}`;

const PriceItemRow = ({ it, idx, count, catCents, onMove, onEdit, onDelete, onMatch, onDragEnd }) => {
  const controls = useDragControls();
  const drift = catCents && catCents !== it.price_cents;
  return (
    <Reorder.Item value={it} as="div" dragListener={false} dragControls={controls} onDragEnd={onDragEnd}
      className="px-3 py-2 rounded-xl bg-[#F5F6F8] border border-[#E4E6EA]" data-testid={`fd-item-${it.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span onPointerDown={(e) => controls.start(e)} title="Drag to reorder"
          className="cursor-grab active:cursor-grabbing touch-none shrink-0 text-[#94979C] hover:text-[#000000] select-none"
          data-testid={`fd-item-drag-${it.id}`}>
          <DotsSixVertical size={20} weight="bold" />
        </span>
        <div className="min-w-0 flex-1">
          <span className="font-semibold text-sm">{it.name}</span>
          <span className="text-sm text-[#000000] ml-2">{money(it.price_cents)}</span>
          {it.sessions > 0 && <span className="text-xs ml-2 px-1.5 py-0.5 rounded-full bg-[#E0E7FF] text-[#3730A3]">{it.sessions}-session pack</span>}
          {it.grants_course_id && <span className="text-xs ml-2 px-1.5 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534]">+ online course</span>}
          {it.description && <div className="text-xs text-[#000000] truncate">{it.description}</div>}
        </div>
        <div className="flex gap-1.5 shrink-0 items-center">
          <div className="flex flex-col">
            <button className="bg-transparent border-0 cursor-pointer text-[#000000] hover:text-[#000000] leading-none p-0.5 disabled:opacity-25"
              disabled={idx === 0} onClick={() => onMove(idx, -1)} title="Move up" data-testid={`fd-item-up-${it.id}`}>▲</button>
            <button className="bg-transparent border-0 cursor-pointer text-[#000000] hover:text-[#000000] leading-none p-0.5 disabled:opacity-25"
              disabled={idx === count - 1} onClick={() => onMove(idx, 1)} title="Move down" data-testid={`fd-item-down-${it.id}`}>▼</button>
          </div>
          <button className="btn-secondary text-xs" style={{ padding: "3px 12px" }} data-testid={`fd-item-edit-${it.id}`}
            onClick={() => onEdit(it)}>Edit</button>
          <button className="btn-secondary text-xs" style={{ padding: "3px 12px", background: "#FEE2E2", color: "#B91C1C", borderColor: "#FCA5A5" }}
            data-testid={`fd-item-del-${it.id}`} onClick={() => onDelete(it)}>Delete</button>
        </div>
      </div>
      {drift && (
        <div className="mt-1.5 flex items-center gap-2 flex-wrap text-xs px-2 py-1.5 rounded-lg bg-[#FEF3C7] border border-[#FCD34D] text-[#92400E]" data-testid={`fd-item-drift-${it.id}`}>
          ⚠️ Poster says {money(it.price_cents)} but your catalogue says <b>{money(catCents)}</b>
          <button className="underline underline-offset-2 font-bold bg-transparent border-0 cursor-pointer text-[#92400E] p-0"
            onClick={() => onMatch(it, catCents)} data-testid={`fd-item-drift-fix-${it.id}`}>Match catalogue</button>
        </div>
      )}
    </Reorder.Item>
  );
};

export default function FrontDeskPanel() {
  const origin = window.location.origin;
  const [items, setItems] = useState([]);
  const [packs, setPacks] = useState([]);
  const [txs, setTxs] = useState(null);
  const [txSource, setTxSource] = useState("stripe");
  const [edit, setEdit] = useState(null); // {id?, name, description, price, sessions}
  const [catPick, setCatPick] = useState(null); // null | flattened catalogue entries
  const [busy, setBusy] = useState(null);
  const [contacts, setContacts] = useState({}); // charge_id -> input value
  const [packLog, setPackLog] = useState(null); // charge_id currently picking a pack item
  const [year, setYear] = useState(new Date().getFullYear());
  const [reviewUrl, setReviewUrl] = useState("");
  const [catPrices, setCatPrices] = useState({}); // name -> price_cents (for drift warnings)
  const [courses, setCourses] = useState([]);
  const [vouchers, setVouchers] = useState([]);

  const flattenCatalog = (data) => {
    const flat = [];
    (data || []).forEach((p) => {
      const priced = (p.variations || []).filter((v) => v.price_cents);
      if (priced.length > 0) {
        priced.forEach((v) => flat.push({ name: priced.length > 1 || (p.variations || []).length > 1 ? `${p.name} — ${v.label}` : p.name, price_cents: v.price_cents }));
      } else if (p.base_price_cents) {
        flat.push({ name: p.name, price_cents: p.base_price_cents });
      }
    });
    return flat;
  };

  const load = async () => {
    try {
      const [i, p] = await Promise.all([api.get("/admin/front-desk/items"), api.get("/admin/front-desk/packs")]);
      setItems(i.data); setPacks(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
    api.get("/site/review-info").then((r) => setReviewUrl(r.data.review_url || "")).catch(() => {});
    api.get("/courses").then((r) => setCourses(r.data || [])).catch(() => {});
    api.get("/admin/front-desk/vouchers").then((r) => setVouchers(r.data || [])).catch(() => {});
    try {
      const { data } = await api.get("/admin/catalog");
      const map = {};
      flattenCatalog(data).forEach((c) => { map[c.name.trim().toLowerCase()] = c.price_cents; });
      setCatPrices(map);
    } catch { /* drift check is best-effort */ }
  };
  const loadTxs = async () => {
    try { const { data } = await api.get("/admin/front-desk/transactions?days=90"); setTxs(data.transactions); setTxSource(data.source); }
    catch (e) { setTxs([]); toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); loadTxs(); }, []);

  const openCatalogue = async () => {
    if (catPick) { setCatPick(null); return; }
    try {
      const { data } = await api.get("/admin/catalog");
      const flat = [];
      (data || []).forEach((p) => {
        const sessionsGuess = (() => {
          const m = (p.name || "").match(/(\d+)[\s-]*(pack|session)/i);
          return m ? parseInt(m[1], 10) : 0;
        })();
        const priced = (p.variations || []).filter((v) => v.price_cents);
        if (priced.length > 0) {
          priced.forEach((v) => flat.push({
            name: priced.length > 1 || (p.variations || []).length > 1 ? `${p.name} — ${v.label}` : p.name,
            description: p.description || "",
            price_cents: v.price_cents,
            sessions: sessionsGuess,
            product_id: p.id,
            variation_id: v.id,
          }));
        } else if (p.base_price_cents) {
          flat.push({ name: p.name, description: p.description || "", price_cents: p.base_price_cents, sessions: sessionsGuess, product_id: p.id, variation_id: null });
        }
      });
      if (flat.length === 0) { toast.info("Your Master catalogue has no priced items yet (Money tab → Master catalogue)"); return; }
      setCatPick(flat);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const matchCatalogue = async (it, catCents) => {
    try {
      await api.put(`/admin/front-desk/items/${it.id}`, { ...it, price_cents: catCents });
      toast.success(`${it.name} updated to ${money(catCents)} — poster follows automatically`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const itemsRef = useRef([]);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const persistOrder = async () => {
    try { await api.post("/admin/front-desk/items/reorder", { ordered_ids: itemsRef.current.map((i) => i.id) }); }
    catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const moveItem = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    try { await api.post("/admin/front-desk/items/reorder", { ordered_ids: arr.map((i) => i.id) }); }
    catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const saveItem = async () => {
    const body = { name: edit.name, description: edit.description, sessions: parseInt(edit.sessions || 0, 10),
      price_cents: Math.round(parseFloat(edit.price || 0) * 100), active: true,
      grants_course_id: edit.grants_course_id || null,
      catalog_product_id: edit.catalog_product_id || null,
      catalog_variation_id: edit.catalog_variation_id || null };
    try {
      if (edit.id) await api.put(`/admin/front-desk/items/${edit.id}`, body);
      else await api.post("/admin/front-desk/items", body);
      toast.success("Price list updated — the poster updates automatically");
      setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delItem = async (it) => {
    try { await api.delete(`/admin/front-desk/items/${it.id}`); toast.success(`${it.name} removed`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const consumePackSession = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post(`/admin/front-desk/packs/${p.id}/use`);
      toast.success(`Session used — ${data.sessions_remaining} left on ${p.item_name}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const sendReceipt = async (tx) => {
    const contact = (contacts[tx.id] || "").trim();
    if (!contact) { toast.error("Enter their email or phone first"); return; }
    setBusy(tx.id);
    try {
      await api.post(`/admin/front-desk/transactions/${tx.id}/receipt`, { contact });
      toast.success(contact.includes("@") ? "Receipt emailed" : "Receipt texted");
      loadTxs();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const noReceipt = async (tx) => {
    try {
      await api.post(`/admin/front-desk/transactions/${tx.id}/no-receipt`, { no_receipt: !tx.no_receipt });
      loadTxs();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendSummary = async (tx) => {
    const contact = (contacts[tx.id] || "").trim();
    if (!contact) { toast.error("Enter their email or phone first"); return; }
    setBusy(`sum-${tx.id}`);
    try {
      const { data } = await api.post("/admin/front-desk/summary", { contact });
      toast.success(`Summary of ${data.count} purchase(s) sent`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const logAsPack = async (tx, item) => {
    setBusy(`pk-${tx.id}`);
    const contact = (contacts[tx.id] || "").trim();
    try {
      const { data } = await api.post(`/admin/front-desk/transactions/${tx.id}/log-pack`, {
        item_id: item.id,
        name: tx.billing_name || "",
        email: contact.includes("@") ? contact : tx.billing_email || "",
        phone: contact && !contact.includes("@") ? contact : "",
      });
      toast.success(data.vouchers
        ? `${item.name} logged — access code${data.vouchers > 1 ? "s" : ""} texted/emailed to the customer`
        : `${item.name} pack logged — session tracker created`);
      setPackLog(null);
      load(); loadTxs();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const resendVoucher = async (v) => {
    let contact = "";
    if (!v.buyer_email && !v.buyer_phone) {
      contact = window.prompt("No contact on file — enter their email or phone:") || "";
      if (!contact.trim()) return;
    }
    setBusy(`rv-${v.id}`);
    try {
      const { data } = await api.post(`/admin/front-desk/vouchers/${v.id}/resend`, { contact });
      toast.success(`Code resent by ${data.sent.join(" + ")}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/admin/front-desk/export?year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `stripe-transactions-${year}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="front-desk-panel">
      {/* Registration + poster */}
      <div className="card" data-testid="fd-walkin-card">
        <h3 className="text-lg font-bold font-display flex items-center gap-2 mb-1"><QrCode size={20} weight="duotone" color="#00E5FF" /> Walk-in registration</h3>
        <p className="text-sm text-[#000000] mb-3">Put this QR on the wall (or pull it up on your phone). New people scan it, sign the waiver once, and never sign again — for anything.</p>
        <div className="flex items-center gap-5 flex-wrap">
          <div className="bg-white p-3 rounded-xl border border-[#C4C8CE]"><QRCodeSVG value={`${origin}/register`} size={130} level="H" data-testid="frontdesk-register-qr" /></div>
          <div className="flex flex-col gap-2">
            <button className="btn-secondary text-sm flex items-center gap-1.5" data-testid="open-register-page"
              onClick={() => window.open("/register", "_blank", "noopener")}><ArrowSquareOut size={15} /> Open registration page</button>
            <button className="btn-secondary text-sm" data-testid="copy-register-link"
              onClick={() => { navigator.clipboard.writeText(`${origin}/register`); toast.success("Link copied"); }}>Copy link</button>
            <button className="btn-primary text-sm flex items-center gap-1.5" data-testid="open-poster"
              onClick={() => window.open("/poster", "_blank", "noopener")}>🖨 Open printable price poster</button>
            <button className="btn-primary text-sm flex items-center gap-1.5" data-testid="open-till"
              onClick={() => window.open("/frontdesk", "_blank", "noopener")}>📱 Open Front Desk mode (phone till)</button>
            <button className="btn-secondary text-sm" data-testid="open-review-poster"
              onClick={() => window.open("/review-poster", "_blank", "noopener")}>⭐ Print Google-review poster</button>
            <div className="flex items-center gap-1.5">
              <input className="input-base text-xs py-1.5 w-56" placeholder="Google review link" value={reviewUrl}
                onChange={(e) => setReviewUrl(e.target.value)} data-testid="review-url-input" />
              <button className="btn-secondary text-xs" data-testid="review-url-save"
                onClick={async () => {
                  try { await api.put("/admin/front-desk/review-settings", { review_url: reviewUrl }); toast.success("Review link saved — texts, receipts, till & poster all use it"); }
                  catch (e) { toast.error(formatApiError(e)); }
                }}>Save</button>
            </div>
          </div>
        </div>
      </div>

      {/* Price list */}
      <div className="card" data-testid="fd-price-list-card">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold font-display flex items-center gap-2 m-0"><Storefront size={20} weight="duotone" color="#CCFF00" /> Price list (poster items)</h3>
          <div className="flex gap-1.5">
            <button className="btn-secondary text-xs" data-testid="add-from-catalogue-btn" onClick={openCatalogue}>
              {catPick ? "Close catalogue" : "+ From catalogue"}
            </button>
            <button className="btn-primary text-xs" data-testid="add-item-btn"
              onClick={() => setEdit({ name: "", description: "", price: "", sessions: 0 })}>+ Add item</button>
          </div>
        </div>
        <p className="text-xs text-[#000000] mb-3">You build your packages — each item gets its own purchase QR on the poster. Set "sessions" for packs (e.g. 10) and the app tracks what's left.</p>
        {catPick && (
          <div className="mb-3 p-3 rounded-xl border border-[#BFDBFE] bg-[#EFF6FF]" data-testid="catalogue-picker">
            <div className="text-xs font-bold text-[#1E40AF] mb-2">Pick from your Master catalogue — it prefills the form so you can tweak price, description or sessions before saving:</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {catPick.map((c, i) => (
                <button key={i} className="px-3 py-2 rounded-xl text-xs font-semibold bg-white border border-[#93C5FD] text-[#1E3A8A] hover:border-[#2563EB] cursor-pointer text-left flex items-center justify-between gap-2"
                  data-testid={`catalogue-pick-${i}`}
                  onClick={() => { setEdit({ name: c.name, description: c.description, price: (c.price_cents / 100).toFixed(2), sessions: c.sessions || 0, catalog_product_id: c.product_id, catalog_variation_id: c.variation_id }); setCatPick(null); }}>
                  <span className="truncate">{c.name}</span>
                  <span className="shrink-0 font-bold">{money(c.price_cents)}{c.sessions > 0 ? ` · ${c.sessions}s` : ""}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {items.length === 0 && <div className="text-sm text-[#000000]">No items yet — add "The Single $100", "The Foundation 10-pack $750"…</div>}
        <Reorder.Group axis="y" values={items} onReorder={setItems} as="div" className="flex flex-col gap-1.5">
          {items.map((it, idx) => (
            <PriceItemRow key={it.id} it={it} idx={idx} count={items.length}
              catCents={catPrices[(it.name || "").trim().toLowerCase()]}
              onMove={moveItem} onDragEnd={persistOrder}
              onEdit={(i) => setEdit({ id: i.id, name: i.name, description: i.description, price: (i.price_cents / 100).toFixed(2), sessions: i.sessions, grants_course_id: i.grants_course_id || "" })}
              onDelete={delItem} onMatch={matchCatalogue} />
          ))}
        </Reorder.Group>
        {edit && (
          <div className="mt-3 p-3 rounded-xl border border-[#CCFF00] bg-[rgba(204,255,0,0.06)]" data-testid="fd-item-editor">
            <div className="grid sm:grid-cols-2 gap-2">
              <input className="input-base text-sm" placeholder="Name — e.g. The Foundation (10 Sessions)" value={edit.name}
                onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="fd-item-name" />
              <input className="input-base text-sm" placeholder="Price — e.g. 750" type="number" min="1" step="0.01" value={edit.price}
                onChange={(e) => setEdit({ ...edit, price: e.target.value })} data-testid="fd-item-price" />
              <input className="input-base text-sm" placeholder="Description (shows on poster)" value={edit.description}
                onChange={(e) => setEdit({ ...edit, description: e.target.value })} data-testid="fd-item-desc" />
              <input className="input-base text-sm" placeholder="Sessions in pack (0 = single purchase)" type="number" min="0" value={edit.sessions}
                onChange={(e) => setEdit({ ...edit, sessions: e.target.value })} data-testid="fd-item-sessions" />
              <select className="input-base text-sm sm:col-span-2" value={edit.grants_course_id || ""}
                onChange={(e) => setEdit({ ...edit, grants_course_id: e.target.value })} data-testid="fd-item-course">
                <option value="">No online course included</option>
                {courses.map((c) => <option key={c.id} value={c.id}>Includes online course: {c.title}</option>)}
              </select>
            </div>
            {edit.grants_course_id && (
              <p className="text-[11px] text-[#166534] mt-1.5 mb-0">When this is paid at the desk, we auto-mint a unique access code per unit and text/email it to the buyer. If they sign up with the same email, the course unlocks automatically.</p>
            )}
            <div className="flex gap-2 mt-2">
              <button className="btn-primary text-xs" onClick={saveItem} data-testid="fd-item-save">Save</button>
              <button className="btn-ghost text-xs" onClick={() => setEdit(null)}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Packs */}
      <div className="card" data-testid="fd-packs-card">
        <h3 className="text-lg font-bold font-display flex items-center gap-2 mb-1"><Ticket size={20} weight="duotone" color="#F87171" /> Session packs</h3>
        <p className="text-xs text-[#000000] mb-3">Every paid pack shows here — tap "Use 1" each time the person trains.</p>
        {packs.length === 0 && <div className="text-sm text-[#000000]">No packs sold yet.</div>}
        <div className="flex flex-col gap-1.5">
          {packs.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-[#F5F6F8] border border-[#E4E6EA]" data-testid={`fd-pack-${p.id}`}>
              <div className="min-w-0 text-sm">
                <span className="font-semibold">{p.customer_name || p.customer_email || p.customer_phone || "Customer"}</span>
                <span className="text-[#000000] ml-2">{p.item_name}</span>
                <div className="text-xs text-[#000000]">{p.customer_email}{p.customer_email && p.customer_phone ? " · " : ""}{p.customer_phone}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={"font-display font-bold text-sm " + (p.sessions_remaining > 0 ? "text-[#166534]" : "text-[#B91C1C]")} data-testid={`fd-pack-remaining-${p.id}`}>
                  {p.sessions_remaining}/{p.sessions_total} left
                </span>
                <button className="btn-secondary text-xs" style={{ padding: "3px 12px" }} disabled={busy === p.id || p.sessions_remaining <= 0}
                  onClick={() => consumePackSession(p)} data-testid={`fd-pack-use-${p.id}`}>Use 1</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Course access codes */}
      {vouchers.length > 0 && (
        <div className="card" data-testid="fd-vouchers-card">
          <h3 className="text-lg font-bold font-display flex items-center gap-2 mb-1"><Ticket size={20} weight="duotone" color="#84cc16" /> Course access codes</h3>
          <p className="text-xs text-[#000000] mb-3">Minted automatically when someone buys online training at the desk. Look one up here if a text/email goes astray — "Used" means it has unlocked an account.</p>
          <div className="flex flex-col gap-1.5">
            {vouchers.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-[#F5F6F8] border border-[#E4E6EA]" data-testid={`fd-voucher-${v.id}`}>
                <div className="min-w-0 text-sm">
                  <span className="font-mono font-bold">{v.code}</span>
                  <span className="text-[#000000] ml-2">{v.item_name}</span>
                  <div className="text-xs text-[#000000] truncate">{v.buyer_email || v.buyer_phone || "—"}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!v.used_by && (
                    <button className="btn-secondary text-xs" style={{ padding: "3px 10px" }} disabled={busy === `rv-${v.id}`}
                      onClick={() => resendVoucher(v)} data-testid={`fd-voucher-resend-${v.id}`}>
                      {busy === `rv-${v.id}` ? "Sending…" : "Resend code"}
                    </button>
                  )}
                  <span className={"text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full " +
                    (v.used_by ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEF3C7] text-[#92400E]")} data-testid={`fd-voucher-status-${v.id}`}>
                    {v.used_by ? "Used ✓" : "Unused"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transactions */}
      <div className="card" data-testid="fd-transactions-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <h3 className="text-lg font-bold font-display flex items-center gap-2 m-0"><Receipt size={20} weight="duotone" color="#D99E02" /> Transactions — one source of truth</h3>
          <div className="flex items-center gap-1.5">
            <input className="input-base text-xs w-20 py-1" type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="fd-export-year" />
            <button className="btn-secondary text-xs flex items-center gap-1" onClick={exportCsv} data-testid="fd-export-csv"><DownloadSimple size={13} /> Year-end CSV</button>
          </div>
        </div>
        <p className="text-xs text-[#000000] mb-3">
          Every Stripe charge from the last 90 days — QR payments, online courses, invoices <b>and Tap to Pay taken in the Stripe app on your phone</b> — all in one list, nothing double-counted.
          Enter their email or mobile then "Receipt" (single) or "Summary" (all their purchases).
        </p>
        {txSource === "local" && (
          <div className="mb-3 p-2.5 rounded-xl bg-[#FEF3C7] border border-[#FCD34D] text-xs text-[#92400E]" data-testid="fd-tx-local-note">
            Test mode: showing this app's own charges. Once you claim your live Stripe account, this list automatically becomes your FULL Stripe history (including Tap to Pay from the Stripe app).
          </div>
        )}
        {txs === null && <div className="text-sm text-[#000000]">Loading Stripe transactions…</div>}
        {txs && txs.length === 0 && <div className="text-sm text-[#000000]">No transactions in the last 90 days.</div>}
        <div className="flex flex-col gap-1.5">
          {(txs || []).map((tx) => (
            <div key={tx.id} className="px-3 py-2 rounded-xl bg-[#F5F6F8] border border-[#E4E6EA]" data-testid={`fd-tx-${tx.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 text-sm">
                  <span className="font-display font-bold">{money(tx.amount_cents)}</span>
                  <span className={"text-[10px] font-bold uppercase ml-2 px-1.5 py-0.5 rounded-full " +
                    (tx.status === "paid" ? "bg-[#DCFCE7] text-[#166534]" : tx.status === "refunded" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#FEE2E2] text-[#B91C1C]")}>{tx.status}</span>
                  <span className="text-[#000000] ml-2">{tx.description}</span>
                  <div className="text-xs text-[#000000]">{tx.created.slice(0, 10)}{tx.billing_email ? ` · ${tx.billing_email}` : ""}
                    {tx.receipts_sent.length > 0 && <span className="text-[#166534]"> · receipt sent ✓</span>}
                    {tx.no_receipt && <span className="text-[#92400E]"> · no receipt wanted</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap min-w-0 w-full sm:w-auto">
                  <input className="input-base text-xs py-1 w-full sm:w-40 max-w-full" placeholder="email or 403-555-0123"
                    value={contacts[tx.id] || ""} onChange={(e) => setContacts({ ...contacts, [tx.id]: e.target.value })}
                    data-testid={`fd-tx-contact-${tx.id}`} />
                  <button className="btn-secondary text-xs" style={{ padding: "3px 10px" }} disabled={busy === tx.id}
                    onClick={() => sendReceipt(tx)} data-testid={`fd-tx-receipt-${tx.id}`}>Receipt</button>
                  <button className="btn-secondary text-xs" style={{ padding: "3px 10px" }} disabled={busy === `sum-${tx.id}`}
                    onClick={() => sendSummary(tx)} data-testid={`fd-tx-summary-${tx.id}`}>Summary</button>
                  <button className="btn-ghost text-xs underline underline-offset-2" onClick={() => noReceipt(tx)} data-testid={`fd-tx-noreceipt-${tx.id}`}>
                    {tx.no_receipt ? "wants one after all" : "no receipt"}
                  </button>
                  {!tx.pack_logged && items.some((i) => i.sessions > 0 || i.grants_course_id) && (
                    <button className="btn-ghost text-xs underline underline-offset-2 text-[#3730A3]" data-testid={`fd-tx-logpack-${tx.id}`}
                      onClick={() => setPackLog(packLog === tx.id ? null : tx.id)}>log as pack / course</button>
                  )}
                </div>
              </div>
              {tx.pack_logged && (
                <div className="text-xs text-[#3730A3] mt-1" data-testid={`fd-tx-packlogged-${tx.id}`}>🎟 Logged as pack: {tx.pack_logged}</div>
              )}
              {packLog === tx.id && (
                <div className="mt-2 p-2 rounded-lg bg-[#EEF2FF] border border-[#C7D2FE]" data-testid={`fd-tx-packpicker-${tx.id}`}>
                  <div className="text-[11px] font-bold text-[#3730A3] mb-1.5">What was this? (tip: put their email/phone in the box above first so the pack/access code knows its owner)</div>
                  <div className="flex flex-wrap gap-1.5">
                    {items.filter((i) => i.sessions > 0 || i.grants_course_id).map((i) => (
                      <button key={i.id} className="px-2.5 py-1 rounded-full text-xs font-semibold bg-white border border-[#A5B4FC] text-[#3730A3] hover:border-[#4F46E5] cursor-pointer"
                        disabled={busy === `pk-${tx.id}`} onClick={() => logAsPack(tx, i)} data-testid={`fd-tx-packpick-${tx.id}-${i.id}`}>
                        {i.name} · {i.sessions > 0 ? `${i.sessions} sessions` : "online course"}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-3 p-3 rounded-xl bg-[#EFF6FF] border border-[#BFDBFE] text-xs text-[#1E40AF]" data-testid="fd-tap-hint">
          💳 <b>Tap to Pay:</b> open the free <b>Stripe Dashboard app</b> on your phone → "Charge" → tap their card on the back of your phone.
          The charge lands in the same Stripe account and appears in this list within seconds — send the receipt from here.
        </div>
      </div>
    </div>
  );
}
